import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MagnifyingGlass, BookmarkSimple, List, X, Joystick, ShieldCheck } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useWatchLater } from "@/hooks/useWatchLater";
import { AccountButton } from "@/components/AccountButton";
import { TvModeToggle } from "@/components/TvModeToggle";
import { CastHelpModal } from "@/components/CastHelpModal";
import { fetchLiveCount } from "@/lib/api";
import { LiveTicker } from "@/components/LiveTicker";
import { SearchSuggest } from "@/components/SearchSuggest";

const TABS = [
  { label: "Browse", to: "/" },
  { label: "Free", to: "/", free: true },
  { label: "Live", to: "/live" },
  { label: "Kids", to: "/kids", kids: true },
  { label: "Games", to: "/games" },
  { label: "Store", to: "/store" },
  { label: "Partners", to: "/partners" },
  { label: "Partner Dashboard", to: "/partners/stats" },
];

const GamesLabel = () => (
  <span className="inline-flex items-center gap-1.5">
    Games <Joystick size={15} weight="duotone" aria-hidden="true" />
  </span>
);

export const Header = ({ search, onSearch }) => {
  const [value, setValue] = useState(search || "");
  const [menuOpen, setMenuOpen] = useState(false);
  const { ids } = useWatchLater();
  const location = useLocation();
  const { user } = useAuth() || {};
  const isOwner = !!user?.is_admin;
  const showSearch = typeof onSearch === "function";

  const { data: liveData } = useQuery({
    queryKey: ["live-count"],
    queryFn: fetchLiveCount,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
  });
  const liveCount = liveData?.count || 0;

  useEffect(() => {
    setValue(search || "");
  }, [search]);

  useEffect(() => {
    if (!showSearch) return undefined;
    const t = setTimeout(() => onSearch(value), 350);
    return () => clearTimeout(t);
  }, [value]); // eslint-disable-line

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const tabClasses = (t, active) =>
    `font-mono uppercase tracking-[0.12em] transition-colors duration-300 ${
      active
        ? t.kids
          ? "text-sky-400"
          : "text-amber"
        : t.free
        ? "text-emerald-400 hover:text-emerald-300"
        : t.kids
        ? "text-sky-400/80 hover:text-sky-300"
        : "text-white/60 hover:text-white"
    }`;

  const isActive = (t) => (t.to === "/" ? location.pathname === "/" && !t.free : location.pathname === t.to);

  const LiveLabel = () => (
    <span className="inline-flex items-center gap-1.5">
      Live
      {liveCount > 0 && (
        <span className="inline-flex items-center gap-1 bg-red-600/20 text-red-400 border border-red-500/30 px-1.5 py-0.5 leading-none text-[10px] font-bold">
          <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
          {liveCount}
        </span>
      )}
    </span>
  );

  return (
    <header className="sticky top-0 z-50 backdrop-blur-2xl bg-[#050505]/80 border-b border-white/10">
      <div className="mx-auto max-w-[1600px] min-[2200px]:max-w-[2200px] px-4 sm:px-8 py-3.5 flex items-center gap-4 sm:gap-6">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 shrink-0 group">
          <img src="/reelvault-mark.png" alt="ReelVault" data-testid="brand-logo" className="h-8 sm:h-9 w-auto group-hover:scale-105 transition-transform duration-300 drop-shadow" />
          <span className="font-serif text-xl sm:text-2xl tracking-tight text-[#EDEDED] hidden sm:block">
            Reel<span className="text-crimson">Vault</span>
          </span>
        </Link>

        {/* Desktop inline nav */}
        <nav className="hidden xl:flex items-center gap-0.5" data-testid="tab-nav">
          {TABS.map((t, i) => (
            <Link
              key={`${t.label}-${i}`}
              to={t.to}
              data-testid={`tab-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`whitespace-nowrap px-2.5 2xl:px-3 py-2 text-xs border-b-2 ${isActive(t) ? (t.kids ? "border-sky-400" : "border-amber") : "border-transparent"} ${tabClasses(t, isActive(t))}`}
            >
              {t.label === "Live" ? <LiveLabel /> : t.label === "Games" ? <GamesLabel /> : t.label}
            </Link>
          ))}
        </nav>

        {showSearch && (
          <div className="relative flex-1 min-w-[120px] max-w-[200px] 2xl:max-w-md hidden min-[1400px]:block">
            <SearchSuggest
              value={value}
              setValue={setValue}
              testid="search-input"
              iconClass="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40"
              inputClass="w-full bg-[#111] border border-white/10 pl-11 pr-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:border-crimson focus:ring-1 focus:ring-crimson transition-colors font-sans text-sm"
            />
          </div>
        )}

        {/* Right action cluster */}
        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          <button
            data-testid="quick-search-trigger"
            onClick={() => window.dispatchEvent(new Event("open-quick-search"))}
            aria-label="Quick search"
            title="Quick search ( / )"
            className="flex items-center justify-center border border-white/10 p-2.5 text-white/70 hover:border-amber hover:text-amber transition-colors duration-300"
          >
            <MagnifyingGlass size={18} />
          </button>

          <AccountButton />

          {isOwner && (
            <Link
              to="/admin"
              data-testid="admin-dashboard-link"
              className="hidden sm:flex items-center gap-2 border border-amber/40 px-3 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-amber hover:bg-amber hover:text-black transition-colors duration-300"
            >
              <ShieldCheck size={16} weight="duotone" />
              <span className="rv-hdr-label hidden 2xl:inline">Admin</span>
            </Link>
          )}

          <Link
            to="/vault"
            data-testid="vault-link"
            className="relative flex items-center gap-2 border border-white/10 px-2.5 sm:px-3.5 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-white/70 hover:border-amber hover:text-amber transition-colors duration-300"
          >
            <BookmarkSimple size={16} weight="duotone" />
            <span className="rv-hdr-label hidden sm:inline xl:hidden 2xl:inline whitespace-nowrap">My Vault</span>
            {ids.length > 0 && (
              <span className="bg-amber text-black px-1.5 py-0.5 text-[10px] leading-none" data-testid="vault-count">
                {ids.length}
              </span>
            )}
          </Link>

          {/* Desktop-only extras */}
          <div className="hidden lg:block">
            <CastHelpModal />
          </div>
          <div className="hidden lg:block">
            <TvModeToggle />
          </div>

          {/* Mobile hamburger */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Menu"
            aria-expanded={menuOpen}
            className="xl:hidden flex items-center justify-center border border-white/10 p-2.5 text-white/80 hover:border-amber hover:text-amber transition-colors duration-300"
          >
            {menuOpen ? <X size={18} weight="bold" /> : <List size={18} weight="bold" />}
          </button>
        </div>
      </div>

      {/* Mobile menu drawer */}
      {menuOpen && (
        <div data-testid="mobile-menu" className="xl:hidden border-t border-white/10 bg-[#050505]/98 backdrop-blur-2xl rv-fade">
          <nav className="flex flex-col px-4 py-2" data-testid="tab-nav-mobile">
            {TABS.map((t, i) => (
              <Link
                key={`${t.label}-m-${i}`}
                to={t.to}
                data-testid={`tab-${t.label.toLowerCase().replace(/\s+/g, "-")}-mobile`}
                className={`py-3.5 text-sm border-b border-white/5 ${tabClasses(t, isActive(t))}`}
              >
                {t.label === "Live" ? <LiveLabel /> : t.label === "Games" ? <GamesLabel /> : t.label}
              </Link>
            ))}
            {isOwner && (
              <>
                <Link to="/admin" data-testid="admin-dashboard-link-mobile" className="py-3.5 text-sm border-b border-white/5 font-mono uppercase tracking-[0.12em] text-amber">
                  Admin Dashboard
                </Link>
                <Link to="/admin#ingestion" data-testid="admin-ingestion-link-mobile" className="py-3.5 text-sm border-b border-white/5 font-mono uppercase tracking-[0.12em] text-amber">
                  Ingestion
                </Link>
              </>
            )}
          </nav>

          {showSearch && (
            <div className="relative px-4 pb-4">
              <SearchSuggest
                value={value}
                setValue={setValue}
                testid="search-input-mobile"
                iconClass="absolute left-7 top-[22px] -translate-y-1/2 text-white/40"
                inputClass="w-full bg-[#111] border border-white/10 pl-11 pr-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-crimson focus:ring-1 focus:ring-crimson transition-colors font-sans text-sm"
              />
            </div>
          )}

          <div className="flex items-center gap-3 px-4 pb-5">
            <CastHelpModal />
            <TvModeToggle />
          </div>
        </div>
      )}
      <LiveTicker />
    </header>
  );
};
